import DOMPurify from "dompurify";

export type TEmailBuilderRichTextKind = "paragraph" | "heading" | "label";

const LIST_MARKUP = /<(ul|ol)\b[^>]*>[\s\S]*?<\/\1>/gi;
const LABEL_INLINE_BOLD = /<(?:strong|b)\b/i;
const LABEL_TAGS = ["b", "strong", "em", "i", "u", "s", "strike"];
const BLOCK_BOLD_FONT_WEIGHT = 700;

export const EMAIL_BUILDER_LABEL_EDITOR_FONT_WEIGHT = 400;

export function isEmailBuilderRichTextEmpty(html: string): boolean {
  return html.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").replace(/\u200b/g, "").trim().length === 0;
}

export function emailBuilderRichTextEditorHtml(args: {
  kind: TEmailBuilderRichTextKind;
  html: string;
  headingLevel?: number;
}): string {
  if (args.kind === "label") {
    return `<p>${labelInner(args.html)}</p>`;
  }

  if (args.kind === "heading") {
    const level = args.headingLevel ?? 1;
    return `<h${level}>${headingInner(args.html)}</h${level}>`;
  }

  return paragraphDocumentHtml(args.html);
}

export function emailBuilderRichTextStoredHtml(kind: TEmailBuilderRichTextKind, html: string): string {
  const trimmed = html.trim();
  if (isEmailBuilderRichTextEmpty(trimmed)) {
    return "";
  }

  if (kind === "label") {
    const inner = unwrapTag(trimmed, "p") ?? trimmed;
    const label = sanitizeLabelHtml(labelInner(inner));
    return isEmailBuilderRichTextEmpty(label) ? "" : label;
  }

  if (kind === "heading") {
    const inner = trimmed.match(/^<h[1-6]\b[^>]*>([\s\S]*)<\/h[1-6]>$/i)?.[1] ?? trimmed;
    return isEmailBuilderRichTextEmpty(inner) ? "" : inner;
  }

  return trimmed;
}

function paragraphDocumentHtml(html: string): string {
  const trimmed = html.trim();
  const heading = trimmed.match(/^<h[1-6]\b[^>]*>([\s\S]*)<\/h[1-6]>$/i);
  const source = (heading?.[1] ?? trimmed).trim();
  if (source === "") {
    return "<p></p>";
  }

  let result = "";
  let cursor = 0;
  for (const match of source.matchAll(LIST_MARKUP)) {
    const index = match.index ?? 0;
    result += wrapInline(source.slice(cursor, index));
    result += match[0];
    cursor = index + match[0].length;
  }

  result += wrapInline(source.slice(cursor));
  return result === "" ? "<p></p>" : result;
}

function wrapInline(fragment: string): string {
  const trimmed = fragment.trim();
  if (trimmed === "") {
    return "";
  }

  if (/^<(p|h[1-6]|ul|ol)\b/i.test(trimmed)) {
    return trimmed;
  }

  return `<p>${trimmed}</p>`;
}

function headingInner(html: string): string {
  const trimmed = html.trim();
  const heading = trimmed.match(/^<h[1-6]\b[^>]*>([\s\S]*)<\/h[1-6]>$/i);
  const inner = heading?.[1] ?? unwrapTag(trimmed, "p") ?? trimmed;

  return inner
    .replace(/<li\b[^>]*>/gi, "")
    .replace(/<\/li>/gi, "<br>")
    .replace(/<\/p>\s*<p\b[^>]*>/gi, "<br>")
    .replace(/<\/?(?:ul|ol|p|div|h[1-6])\b[^>]*>/gi, "")
    .replace(/(?:<br\s*\/?>\s*)+$/i, "");
}

export function emailBuilderLabelEditorContent(html: string, fontWeight: number | undefined): string {
  if (html.trim() === "") {
    return html;
  }

  const weight = fontWeight ?? BLOCK_BOLD_FONT_WEIGHT;
  if (weight >= BLOCK_BOLD_FONT_WEIGHT && !LABEL_INLINE_BOLD.test(html)) {
    return `<strong>${html}</strong>`;
  }

  return html;
}

export function emailBuilderDisplayedLabelFontWeight(html: string, fontWeight: number | undefined): number {
  if (LABEL_INLINE_BOLD.test(html)) {
    return EMAIL_BUILDER_LABEL_EDITOR_FONT_WEIGHT;
  }

  return fontWeight ?? BLOCK_BOLD_FONT_WEIGHT;
}

export function emailBuilderLabelCommitUpdate(
  currentText: string,
  currentFontWeight: number | undefined,
  nextLabel: string,
): { text: string; fontWeight: number } | null {
  const weight = currentFontWeight ?? BLOCK_BOLD_FONT_WEIGHT;
  const normalizeWeight = weight >= BLOCK_BOLD_FONT_WEIGHT
    && (currentText.trim() !== "" || nextLabel.trim() !== "");
  if (nextLabel === currentText && !normalizeWeight) {
    return null;
  }

  return {
    text: nextLabel,
    fontWeight: normalizeWeight ? EMAIL_BUILDER_LABEL_EDITOR_FONT_WEIGHT : weight,
  };
}

function labelInner(html: string): string {
  return html.replace(/<\/?(?:a|ul|ol|li|p|div|h[1-6]|br)\b[^>]*>/gi, "");
}

function sanitizeLabelHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: LABEL_TAGS,
    ALLOWED_ATTR: [],
  });
}

function unwrapTag(html: string, tag: string): string | null {
  const match = html.trim().match(new RegExp(`^<${tag}\\b[^>]*>([\\s\\S]*)<\\/${tag}>$`, "i"));
  return match?.[1] ?? null;
}
